import { useSyncExternalStore } from "react"

/**
 * Generic CSS media-query subscription hook. Returns `true` when the
 * `window.matchMedia(query)` predicate currently matches the viewport,
 * `false` otherwise.
 *
 * Why this exists alongside `use-mobile.ts` (which is the existing 768px
 * `useIsMobile` helper): `useIsMobile`'s breakpoint is hardcoded and the
 * hook is consumed in 17+ places at that exact 768px. Parameterising it
 * would be a breaking-change vector. `useMediaQuery` is the focused
 * primitive — pass any standard media-query string, get a reactive
 * boolean. OKS-120's `ResponsiveDialog` wrapper consumes this at the
 * 640px shadcn `sm:` breakpoint (`"(max-width: 639px)"`). The longer-term
 * direction is to refactor `useIsMobile` to a one-liner over this hook;
 * not in scope for OKS-120.
 *
 * SSR-safety mirrors `useIsMobile` verbatim:
 *   - `window` is undefined during Vite SSR, so the `MediaQueryList`
 *     is lazily resolved per call (browsers cache MQL objects internally,
 *     so re-resolving the same query is cheap).
 *   - The `getServerSnapshot` arm returns `false` — there's no "correct"
 *     SSR answer because the server can't know the client's viewport.
 *     The first client render after hydration re-evaluates and updates;
 *     this causes a brief hydration-mismatch warning when the actual
 *     viewport mismatches the SSR assumption, which the codebase
 *     accepts (same trade-off `useIsMobile` carries today).
 *
 * @param query CSS media-query string (e.g. `"(max-width: 639px)"`).
 * @returns `true` when the query matches; `false` otherwise (incl. SSR).
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (callback) => subscribe(query, callback),
    () => getSnapshot(query),
    getServerSnapshot,
  )
}

/**
 * Lazy `MediaQueryList` accessor. `window` is undefined during Vite SSR
 * (Node context), so constructing the MQL at module-evaluation time
 * would throw. Re-resolving here is safe — browsers cache MQL objects
 * internally keyed on the query string, so successive `matchMedia()`
 * calls return the same underlying object.
 */
function getMediaQueryList(query: string): MediaQueryList | null {
  if (typeof window === "undefined") return null
  return window.matchMedia(query)
}

const noopUnsubscribe = () => {
  // Intentionally empty — useSyncExternalStore demands a cleanup function
  // from `subscribe`, but during SSR there's nothing to unsubscribe from.
}

function subscribe(query: string, callback: () => void) {
  const mql = getMediaQueryList(query)
  if (!mql) return noopUnsubscribe
  mql.addEventListener("change", callback)
  return () => {
    mql.removeEventListener("change", callback)
  }
}

function getSnapshot(query: string) {
  return getMediaQueryList(query)?.matches ?? false
}

function getServerSnapshot() {
  // No viewport on the server. Assume desktop-style "doesn't match".
  // Consumers (e.g. ResponsiveDialog) interpret `false` as desktop mode.
  return false
}
